<template src="./template.html"></template>
<script>
import Vue from 'vue'
import _sortBy from 'lodash/sortBy'
import _find from 'lodash/find'
import _filter from 'lodash/filter'
import _cloneDeep from 'lodash/cloneDeep'
import _orderBy from 'lodash/orderBy'
import _sumBy from 'lodash/sumBy'
import Popper from 'popper.js'
import { mapState } from 'vuex'

import layoutService from 'api/layout'
import layoutTableService from 'api/layoutTable'
import currencyUtils from 'src/utils/currentUtils'

export default {
  name: 'inventory-map',
  props: {
    //THEMEING
    theme: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      layouts: null,
      currentLayout: null,
      layoutTables: null,
      popperInstance: null,
      currentPopperTable: null,
      currentPopperInventorySection: null,
    }
  },
  computed: {
    ...mapState({
      date: (state) => state.date.current,
      venue: (state) => state.venue.current,
      inventory: (state) => state.inventory.current,
      layoutSections: (state) => state.layoutSections.current,
    }),
    orderedLayouts() {
      let layouts = _sortBy(this.layouts, 'order', 'desc')

      if (this.venue && this.venue.id === 'cbd997a85c6df50d') {
        //NOTE: filter for SBE Hyde South Beach
        const allowedLayouts = ['65e4c083a92650ba', '191d228a65e1e9c5', '2705c6660654eead']

        layouts = _filter(layouts, (layout) => allowedLayouts.includes(layout.id))
      }

      return layouts
    },
    sectionsWithInventory() {
      let sections = _cloneDeep(this.layoutSections || [])

      sections.forEach((section) => {
        section.inventory = _filter(this.tables, {
          venueLayoutSection: section.id,
        })
      })

      sections = _filter(sections, (section) => section.inventory && section.inventory.length)

      sections = _orderBy(sections, 'name', 'asc')

      return sections
    },
    currentPopperInventorySectionPriceRange() {
      if (!this.currentPopperInventorySection) return ''

      let inventorySectionInventory = _filter(this.inventory, {
        venueLayoutSection: this.currentPopperInventorySection.id,
      })

      inventorySectionInventory = _orderBy(inventorySectionInventory, 'price')

      if (inventorySectionInventory.length === 0) return '$0.00'
      if (inventorySectionInventory.length === 1) {
        return currencyUtils.venueCurrency(inventorySectionInventory[0].price || 0, this.venue.currencyCode, this.venue.languageCode)
      }

      let price = ''

      price += currencyUtils.venueCurrency(inventorySectionInventory[0].price || 0, this.venue.currencyCode, this.venue.languageCode)
      price += ' - '
      price += currencyUtils.venueCurrency((inventorySectionInventory[inventorySectionInventory.length - 1] || 0).price || 0, this.venue.currencyCode, this.venue.languageCode)

      return price
    },
  },
  methods: {
    showPopper(table, $event) {
      this.clearPopper()

      this.unhighlightAllTables()

      this.currentPopperInventorySection = _find(this.layoutSections, {
        id: table.venueLayoutSection,
      })

      if (!this.currentPopperInventorySection) return

      const tableInventory = _filter(this.inventory, {
        venueLayoutSection: table.venueLayoutSection,
      })

      if (!tableInventory || !tableInventory.length) return

      this.currentPopperInventorySection.inventory = _filter(this.inventory, {
        venueLayoutSection: this.currentPopperInventorySection.id,
      })

      const inventory = _filter(this.inventory, (inv) => {
        return inv.venueLayoutSection === this.currentPopperInventorySection.id && inv.quantityLeft > 0
      })

      const quantity = _sumBy(inventory, 'quantityLeft')

      this.currentPopperInventorySection.inventoryQuantity = quantity

      this.highlightTablesByLayoutSectionId(this.currentPopperInventorySection.id, 'popperVisible')

      Vue.nextTick(() => {
        if (this.currentPopperTable === table || !this.currentPopperInventorySection) {
          this.clearPopper()
        } else {
          this.currentPopperTable = table

          Vue.nextTick(() => {
            this.popperInstance = new Popper($event.target, this.$refs.popUp, {
              arrowElement: '[x-arrow]',
              modifiers: {},
            })
          })
        }
      })
    },
    hidePopper() {
      if (this.popperInstance) {
        this.popperInstance.destroy()
        this.popperInstance = null
      }
    },
    clearPopper() {
      if (this.currentPopperInventorySection) {
        this.unhighlightTablesByLayoutSectionId(this.currentPopperInventorySection.id, 'popperVisible')
      }

      this.currentPopperTable = null
      this.currentPopperInventorySection = null

      this.hidePopper()
    },
    onMapBodyClick() {
      if (this.popperInstance) {
        this.clearPopper()
      }
    },
    getVenueLayouts() {
      return layoutService
        .listByVenueId(this.venue.id)
        .then((layouts) => {
          this.layouts = _filter(layouts, (layout) => !!layout.floorPlan)
        })
        .catch(() => {
          this.loading = false
        })
    },
    getVenueLayoutTables() {
      return layoutTableService
        .listByVenueId(this.venue.id)
        .then((layoutTables) => {
          this.layoutTables = _filter(layoutTables, (table) => table.venueLayoutSection)
        })
        .catch(() => {
          this.loading = false
        })
    },
    selectInventorySection(layoutSection) {
      this.clearPopper()

      const data = {
        layoutSection,
      }

      this.$emit('inventorySectionClicked', data)
    },
    highlightTablesByLayoutSectionId(layoutSectionId, propOverride) {
      const layoutSectionTables = _filter(this.layoutTables, {
        venueLayoutSection: layoutSectionId,
      })

      layoutSectionTables.forEach((layoutTable) => {
        if (!propOverride) {
          layoutTable.hovering = true
        } else {
          layoutTable[propOverride] = true
        }

        Vue.set(this.layoutTables, this.layoutTables.indexOf(layoutTable), layoutTable)
      })
    },
    unhighlightTablesByLayoutSectionId(layoutSectionId, propOverride) {
      const layoutSectionTables = _filter(this.layoutTables, {
        venueLayoutSection: layoutSectionId,
      })

      layoutSectionTables.forEach((layoutTable) => {
        if (!propOverride) {
          layoutTable.hovering = false
        } else {
          layoutTable[propOverride] = false
        }

        Vue.set(this.layoutTables, this.layoutTables.indexOf(layoutTable), layoutTable)
      })
    },
    unhighlightAllTables() {
      if (!this.layoutTables || !this.layoutTables.length) return

      this.layoutTables.forEach((layoutTable) => {
        layoutTable.hovering = false
        layoutTable.popperVisible = false

        Vue.set(this.layoutTables, this.layoutTables.indexOf(layoutTable), layoutTable)
      })
    },
    onFloorplanLoaded() {
      this.$emit('floorplanLoaded')
    },
    onTableMouseOver(table) {
      const venueLayoutSection = table.venueLayoutSection

      if (venueLayoutSection) {
        this.highlightTablesByLayoutSectionId(venueLayoutSection)
      }
    },
    onTableMouseOut(table) {
      const venueLayoutSection = table.venueLayoutSection

      if (venueLayoutSection) {
        this.unhighlightTablesByLayoutSectionId(venueLayoutSection)
      }
    },
    doesTableHaveInventory(table) {
      if (!table || !table.venueLayoutSection) return false

      const tableInventory = _filter(this.inventory, {
        venueLayoutSection: table.venueLayoutSection,
      })

      return tableInventory.length > 0
    },
    doesTableHaveInventoryWithQuantity(table) {
      if (!table || !table.venueLayoutSection) return false

      const tableInventory = _filter(this.inventory, (inventory) => {
        return inventory.venueLayoutSection === table.venueLayoutSection && inventory.quantityLeft > 0
      })

      return tableInventory.length < 1
    },
  },
  mounted() {
    Promise.all([this.getVenueLayouts(), this.getVenueLayoutTables(), this.$store.dispatch('getCurrentLayoutSections', this.venue.id)]).then(() => {
      this.currentLayout = this.orderedLayouts[0]
    })
  },
  updated() {
    this.$emit('updated')
  },
}
</script>



// WEBPACK FOOTER //
// src/components/InventoryMap/index.vue